/**
 * ClaudeArtifactsPanel — per-project Claude Artifacts registry.
 *
 * Claude artifacts live on the user's claude.ai account and have no end-user
 * list API, so this lists the artifacts that were PUBLISHED via Claude Code
 * while working in THIS project, reconstructed by the core from the project's
 * Claude Code transcripts (`backend.artifacts.list(projectId)`). Clicking one
 * opens it live in the embedded browser pane (top-level navigation, so the
 * gallery's iframe block doesn't apply and the user's claude.ai login is used).
 *
 * Caveat surfaced in the empty state: this only sees artifacts made via Claude
 * Code in this project — not ones created on claude.ai in a browser.
 */
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, ExternalLink, FileBox, LogIn } from 'lucide-react';
import { useBackend } from '@/services/backend';
import { CefBackend } from '@/services/backend/CefBackend';
import type { ArtifactEntry } from '@/services/backend/IBackend';
import { logger } from '@/lib/logger';

const TAG = 'ClaudeArtifactsPanel';

function age(iso?: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

interface ClaudeArtifactsPanelProps {
  projectId?: string;
}

export default function ClaudeArtifactsPanel({ projectId }: ClaudeArtifactsPanelProps) {
  const backend = useBackend();
  const [items, setItems] = useState<ArtifactEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // This project's Claude identity. When set (a work/non-default account), its
  // artifacts live on a different claude.ai login than personal, so the browser
  // pane opens them in that identity's isolated cookie jar (context=identity).
  // We also offer a one-time "Sign in" that opens a real window in that context.
  const [identity, setIdentity] = useState<string>('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) { setIdentity(''); return; }
      try {
        const raw = await backend.files.readAteliercodeConfig(projectId);
        const id = raw ? (JSON.parse(raw).claudeIdentity as string | undefined) : undefined;
        if (!cancelled) setIdentity(typeof id === 'string' ? id : '');
      } catch {
        if (!cancelled) setIdentity('');
      }
    })();
    return () => { cancelled = true; };
  }, [backend, projectId]);

  // Open a visible sign-in window in this identity's cookie jar so SSO completes
  // in a real window; the login then persists for the composited artifact tabs.
  const signIn = useCallback(() => {
    if (!(backend instanceof CefBackend)) {
      window.open('https://claude.ai/login', '_blank', 'noopener');
      return;
    }
    const ctxKey = identity || 'default';
    void backend.browser
      .openWindow(`claudeai-login-${ctxKey}`, 'https://claude.ai/login', identity)
      .catch((e) => logger.warn(TAG, 'sign-in window failed', e));
  }, [backend, identity]);

  const load = useCallback(async () => {
    if (!projectId) {
      setItems([]);
      return;
    }
    setError(null);
    try {
      setItems(await backend.artifacts.list(projectId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setItems([]);
    }
  }, [backend, projectId]);

  useEffect(() => { void load(); }, [load]);

  const openArtifact = useCallback((url: string) => {
    try {
      const fn = (window as unknown as { __atelierAgentOpenBrowser?: (u: string) => string })
        .__atelierAgentOpenBrowser;
      if (typeof fn === 'function') {
        fn(url);
      } else {
        // No browser pane bridge (e.g. popout); fall back to a new OS tab.
        window.open(url, '_blank', 'noopener');
      }
    } catch (e) {
      logger.warn(TAG, 'open artifact failed', e);
    }
  }, []);

  return (
    <div className="h-full flex flex-col bg-base-100 text-base-content">
      <div className="flex items-center justify-between px-3 py-2 border-b border-base-300 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <FileBox className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">Claude Artifacts</div>
            <div className="text-[11px] text-base-content/50 truncate">
              {identity
                ? `Published from Claude Code · ${identity} account`
                : 'Published from Claude Code in this project'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {identity && (
            <button
              type="button"
              className="btn btn-ghost btn-sm gap-1"
              onClick={signIn}
              title={`Sign in to claude.ai as your "${identity}" account (opens a window; needed once so these artifacts render)`}
            >
              <LogIn className="w-4 h-4" />
              <span className="text-xs">Sign in</span>
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-square"
            onClick={() => void load()}
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {error && <div className="alert alert-warning text-xs py-2 mb-2">{error}</div>}
        {items === null ? (
          <div className="flex justify-center py-8"><span className="loading loading-spinner loading-sm" /></div>
        ) : items.length === 0 ? (
          <div className="text-center text-xs text-base-content/40 py-8 px-4 leading-relaxed">
            No artifacts yet.
            <br />
            Artifacts you publish with Claude Code while working in this project
            will show up here. (Artifacts created on claude.ai in a browser aren't
            visible here.)
          </div>
        ) : (
          items.map((a) => (
            <button
              key={a.artifactId}
              type="button"
              onClick={() => openArtifact(a.url)}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs hover:bg-base-200 text-left group"
              title={a.url}
            >
              <FileBox className="w-3.5 h-3.5 text-base-content/50 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{a.label}</div>
                <div className="text-[10px] text-base-content/45 truncate">{a.url}</div>
              </div>
              {a.createdAt && (
                <span className="text-[10px] font-mono text-base-content/40 shrink-0" title={a.createdAt}>
                  {age(a.createdAt)}
                </span>
              )}
              <ExternalLink className="w-3.5 h-3.5 text-base-content/30 group-hover:text-primary shrink-0" />
            </button>
          ))
        )}
      </div>
    </div>
  );
}
